import type { TFunction } from 'i18next';
import { ExternalService } from '../../../../api/integrations-client/integrations-client.type';
import type { Outcome } from '../../../../api/strategy-client/strategy-client.type';
import type { FilterOption } from '../../../../components/filters/filter-panel.type';
import { primaryBase, redBase, skyDark, yellowBase } from '../../../../styles/design-tokens';
import { InitiativeState, Status } from '../../strategy.type';
import { getStatusLabels, SOURCE_LABELS } from './filters.type';
import { StatusLabel } from './status-label';

/**
 * Single source of truth for which Status values seed the filter UI and the
 * store's default filter state. Module-scope so non-React consumers can read
 * enum values without a translation function.
 */
const STATUS_FILTER_VALUES = [Status.OnTrack, Status.AtRisk, Status.OffTrack, Status.Unknown] as const;

/**
 * Single source of truth for which InitiativeState values seed the
 * active/inactive filter UI and the store's default filter state.
 */
const ACTIVE_STATE_FILTER_VALUES = [InitiativeState.Active, InitiativeState.Inactive] as const;

const STATUS_FILTER_COLORS: Record<(typeof STATUS_FILTER_VALUES)[number], string> = {
  [Status.OnTrack]: primaryBase,
  [Status.AtRisk]: yellowBase,
  [Status.OffTrack]: redBase,
  [Status.Unknown]: skyDark,
};

/**
 * Returns an array of filter options for status filtering
 * Each option contains a value from the Status enum and a styled StatusLabel component
 *
 * Note: The status options are currently hardcoded and may need to be made configurable in the future
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {FilterOption[]} Array of filter options with status values and styled labels
 */
const getStatusOptions = (t: TFunction): FilterOption[] => {
  const labels = getStatusLabels(t);
  return STATUS_FILTER_VALUES.map((value) => ({
    value,
    label: <StatusLabel color={STATUS_FILTER_COLORS[value]} label={labels[value]} />,
  }));
};

/**
 * Returns an array of filter options for initiative state filtering
 * Each option contains a value from the InitiativeState enum and a display label
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {FilterOption[]} Array of filter options with initiative state values and display labels
 */
const getStateOptions = (t: TFunction): FilterOption[] => {
  return [
    {
      value: InitiativeState.NoEpics,
      label: t('strategy.initiatives.no_epics'),
    },
    {
      value: InitiativeState.NoEndDate,
      label: t('strategy.initiatives.no_end_date'),
    },
  ];
};

/**
 * Returns an array of filter options for active/inactive status
 * Each option contains a value from the InitiativeState enum and a display label
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {FilterOption[]} Array of filter options with active/inactive values and display labels
 */
const getActiveStateOptions = (t: TFunction): FilterOption[] => {
  const labels: Record<(typeof ACTIVE_STATE_FILTER_VALUES)[number], string> = {
    [InitiativeState.Active]: t('common.active'),
    [InitiativeState.Inactive]: t('common.inactive'),
  };
  return ACTIVE_STATE_FILTER_VALUES.map((value) => ({ value, label: labels[value] }));
};

/**
 * Returns an array of filter options for source filtering
 * Each option contains a value (either 'bloomfilter' or an ExternalService enum value) and a display label
 *
 * Note: The source options are currently hardcoded and may need to be made configurable in the future
 *
 * @returns {FilterOption[]} Array of filter options with source values and display labels
 */
const getSourceOptions = (): FilterOption[] => {
  return [
    { value: 'bloomfilter', label: 'Bloomfilter' },
    { value: ExternalService.Jira, label: SOURCE_LABELS[ExternalService.Jira] },
    { value: ExternalService.JDC, label: SOURCE_LABELS[ExternalService.JDC] },
    { value: ExternalService.ADO, label: SOURCE_LABELS[ExternalService.ADO] },
    { value: ExternalService.Github, label: SOURCE_LABELS[ExternalService.Github] },
  ];
};

/**
 * Returns an array of filter options for parent outcome filtering
 * Each option contains an outcome ID and a label with the outcome key and name
 *
 * @param {Outcome[]} outcomes - Array of outcomes to convert to filter options
 * @returns {FilterOption[]} Array of filter options with outcome values and display labels
 */
const getOutcomeOptions = (outcomes: Outcome[]): FilterOption[] => {
  return outcomes.map((outcome) => ({
    value: outcome.id,
    label: outcome.key ? `${outcome.key}: ${outcome.name}` : outcome.name,
  }));
};

export {
  ACTIVE_STATE_FILTER_VALUES,
  getActiveStateOptions,
  getOutcomeOptions,
  getSourceOptions,
  getStateOptions,
  getStatusOptions,
  STATUS_FILTER_VALUES,
};
